/**
 * Goal planners — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_trackers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const goalPlannersPack: ToilePack = {
    slug: 'goal-planners',
    category: 'planner',
    name: 'Goal planners',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'yearly', label: 'Yearly goals', subject: 'a yearly goals page with six boxes "Health", "Money", "Career", "Family", "Learning" and "Fun", each with three blank lines'},
        {key: 'steps', label: 'Goal steps', subject: 'a goal planner page with a large blank "My Goal" box, a "Why" line, five numbered action steps with empty deadline boxes and a reward line at the foot'},
        {key: 'monthly', label: 'Monthly tracker', subject: 'a monthly goal tracker titled "My Goals", goals down the left in six blank rows and 31 numbered day columns of small empty tick boxes'},
    ],
    styles: [
        {key: 'bold', label: 'Bold modern', style: 'drawn with heavy sans-serif headings, solid section blocks and large boxes', palette: 'Deep navy and bright orange on white paper.', framing: 'Laid out on a portrait page with an even margin all round, print-ready.'},
        {key: 'soft', label: 'Soft watercolor', style: 'drawn with loose watercolor washes behind the headings and fine hand lettering', palette: 'Peach, lavender and sage washes on cream paper.', framing: 'Laid out on a portrait page with an even margin all round, print-ready.'},
        {key: 'minimal', label: 'Minimal lines', style: 'drawn with hairline rules, small caps headings and wide white space', palette: 'Black ink with a single sage accent on white paper.', framing: 'Laid out on a portrait page with an even margin all round, print-ready.'},
    ],
    directions: ['for students', 'new year', '5 year plan'],
    directionHint: 'try: for students',
    seo: {
        title: 'Goal Planner: Yearly Goals, Goal Steps, Monthly Tracker',
        metaDescription: 'Nine goal planner pages: a yearly goals sheet, a single goal broken into steps and a monthly goal tracker, in three styles.',
        h1: 'Goal planner: yearly goals, goal broken into steps, monthly goal tracker',
        tagline: 'Three goal planners, three styles each, with every goal and step left blank.',
        intro: 'A goal planner is where you write each goal down with its steps and a date, for the year or the quarter. These nine show three pages: a yearly goals sheet with boxes for health, money, career, family, learning and fun, a single goal page with a why line and five numbered steps, and a monthly tracker with six goal rows and 31 day columns. Type a direction, such as new year, and the planners follow it.',
        useCases: [
            'a goal sheet for the new year',
            'one goal broken into steps',
            'a monthly check on several goals',
            'a five-year plan',
        ],
        faq: [
            {q: 'What is on the yearly goals sheet?', a: 'Six boxes, for health, money, career, family, learning and fun, each with blank lines for goals.'},
            {q: 'What is the difference between the three styles?', a: 'Bold modern has heavy sans-serif headings and solid section blocks. Soft watercolor has loose washes behind the headings. Minimal lines has hairline rules and wide white space.'},
            {q: 'Can I change the goals?', a: 'Yes. Type a direction such as for students. A direction is a request, not a guaranteed result.'},
        ],
    },
}
